import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { AuthContext } from "../../../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../../../utils/constants/permissions";
import { isFreelance } from "../../../../../utils/constants/accountType";
import { btn } from "../../../../../components/Button/buttonStyles";

const TYPE_BADGE = {
  Company: "bg-amber-50 text-amber-700",
  Individual: "bg-sky-50 text-sky-700",
};

const STATUS_BADGE = {
  Active: "bg-emerald-50 text-emerald-700",
  Inactive: "bg-gray-100 text-gray-600",
  Pending: "bg-amber-50 text-amber-700",
};

const TypeBadge = ({ type }) => {
  if (!type) return <span className="text-gray-500">—</span>;
  const cls = TYPE_BADGE[type] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>
      {type}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-500">—</span>;
  const cls = STATUS_BADGE[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>
      {status}
    </span>
  );
};

const Pill = ({ enabled }) => (
  <span
    className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${
      enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {enabled ? "Enabled" : "Disabled"}
  </span>
);

const SectionHeader = ({ title, children }) => (
  <div className="flex justify-between items-center mb-3">
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
      {title}
    </h3>
    {children && <div className="flex items-center gap-2 text-sm">{children}</div>}
  </div>
);

const Card = ({ children }) => (
  <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
    {children}
  </div>
);

const btnPrimary = btn("primary", "sm");
const btnSecondary = btn("secondary", "sm");
const btnDanger = btn("danger", "sm");
const btnWarning = btn("warning", "sm");

const ClientView = ({
  client,
  onEditGeneral,
  onEditPrimary,
  onEditSettings,
  onDelete,
  onResendInvitation,
  resendingInvitation = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Account managers view client records without being able to change them.
  // The Project module is absent from their portal too, so "New Project" goes
  // with the edit controls.
  const readOnly = isReadOnly(user, MODULES.CLIENTS);

  const handleConfirmDelete = () => {
    setIsModalOpen(false);
    onDelete();
  };

  const handleNewProject = () => {
    navigate(`/projects/create-project?clientId=${client.id}`);
  };

  const primaryUser = client.primary_user || {};
  // Freelance accounts cannot grant clients portal access, so there is no
  // login state to show and nothing to re-invite.
  const freelance = isFreelance(user);
  const showResend = !freelance && client.can_login && !client.auth?.is_active;

  return (
    <div className="space-y-5">
      <section>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-bold text-gray-900">General Info</h2>

          <div className="flex items-center gap-2 text-sm">
            {!readOnly && (
              <>
                <button className={btnPrimary}>New Quote</button>
                <button onClick={handleNewProject} className={btnPrimary}>New Project</button>
              </>
            )}
            <button className={btnSecondary}>Quick Links ▾</button>
            {!readOnly && (
              <>
                <button onClick={onEditGeneral} className={btnPrimary}>
                  Update
                </button>
                <button onClick={() => setIsModalOpen(true)} className={btnDanger}>
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        <Card>
          <div className="space-y-3">
            <Field label="Type" value={<TypeBadge type={client.type} />} />
            <Field label="Company Name" value={client.company_name} />
            {!freelance && (
              <Field label="Legal Entity" value={client.legal_entity} />
            )}
            <Field label="Status" value={<StatusBadge status={client.status} />} />
            <Field label="PAN / Tax Number" value={client.pan_tax_number} />
            <Field label="GSTIN / VAT Number" value={client.gstin_vat_number} />
            <Field
              label="Website"
              value={
                client.website ? (
                  <a
                    href={client.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-600 hover:underline"
                  >
                    {client.website}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            <Field label="Note" value={client.note} />
          </div>
          <div className="space-y-3">
            <Field label="Country" value={client.country} />
            <Field label="State/Province/Region" value={client.state_region} />
            <Field label="City" value={client.city} />
            <Field label="Address" value={client.address} />
            <Field label="Postal/ZIP Code" value={client.postal_code} />
            <Field
              label="Created At"
              value={
                client.createdAt
                  ? new Date(client.createdAt).toLocaleString()
                  : "—"
              }
            />
          </div>
        </Card>
      </section>

      <section>
        <SectionHeader title="Primary User Details">
          {showResend && !readOnly && (
            <button
              onClick={onResendInvitation}
              disabled={resendingInvitation}
              className={btnWarning}
            >
              {resendingInvitation ? "Sending..." : "Resend Invitation"}
            </button>
          )}
          {!readOnly && (
            <button onClick={onEditPrimary} className={btnPrimary}>
              Update
            </button>
          )}
        </SectionHeader>

        <Card>
          <div className="space-y-3">
            <Field
              label="Name"
              value={`${primaryUser.first_name || ""} ${
                primaryUser.last_name || ""
              }`.trim()}
            />
            <Field label="Email" value={client.auth?.email} />
            <Field label="Timezone" value={primaryUser.timezone} />
            <Field label="Phone" value={primaryUser.phone} />
            <Field label="Zoom ID" value={primaryUser.zoom_id} />
            <Field label="Teams ID" value={primaryUser.teams_id} />
          </div>
          <div className="space-y-3">
            <Field label="Gender" value={primaryUser.gender} />
            <Field label="Nationality" value={primaryUser.nationality} />
            {!freelance && (
              <Field label="Can Login" value={<Pill enabled={client.can_login} />} />
            )}
            <Field
              label="Invited At"
              value={
                client.invitedAt
                  ? new Date(client.invitedAt).toLocaleString()
                  : "—"
              }
            />
            <Field
              label="Last Activity At"
              value={
                client.lastActivityAt
                  ? new Date(client.lastActivityAt).toLocaleString()
                  : "—"
              }
            />
          </div>
        </Card>
      </section>

      <section>
        <SectionHeader title="Settings">
          {!readOnly && (
            <button onClick={onEditSettings} className={btnPrimary}>
              Update
            </button>
          )}
        </SectionHeader>

        <Card>
          <div className="space-y-3">
            <Field label="Client Manager" value="—" />
            <Field label="Default Legal Entity" value="—" />
            <Field label="Default Currency" value="—" />
            <Field label="Default Payment Method" value="—" />
          </div>
          <div className="space-y-3">
            <Field label="Default Payment Terms" value="—" />
            {/* <Field label="Default CAT Log Conversion Scheme" value="-" /> */}
            <Field label="Default Invoice PDF Template" value="—" />
          </div>
        </Card>
      </section>

      {isModalOpen && (
        <ConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to delete this client?"
          onCancel={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}
          confirmText="Yes, Delete"
        />
      )}
    </div>
  );
};

const Field = ({ label, value }) => {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className="flex items-start text-sm">
      <span className="w-40 shrink-0 text-xs font-medium text-gray-500 pt-px">
        {label}:
      </span>
      <span className="text-gray-900">{isEmpty ? "—" : value}</span>
    </div>
  );
};

export default ClientView;
